import React, { MouseEventHandler, useCallback, useMemo, useRef } from "react";
import { NextSeo } from "next-seo";
import useAssetPixelate from "domains/assets/hooks/useAssetPixelate";
import { useAssetPixelateCu } from "domains/assets/hooks/useAssetPixelateCu";
import FileManagerImage from "domains/file-manager/components/FileManagerImage";
import { FmImageCardActionsProps } from "domains/file-manager/components/FileManagerImage/Card/Actions";
import { FmImageListActionsProps } from "domains/file-manager/components/FileManagerImage/List/Actions";
import MoreTooltip from "domains/file-manager/components/FileManagerImage/List/MoreTooltip";
import { FILTER_IMAGE_TYPES_KEYS_ONLY_GENERATIONS } from "domains/file-manager/constants/AssetFilter";
import { useGetFileImagePlaceholders } from "domains/file-manager/hooks/useGetFileImagePlaceholders";
import SidebarSection from "domains/inference/components/InferenceGenerator/Sidebar/Section";
import SectionReferenceImageBase from "domains/inference/components/InferenceGenerator/Sidebar/SectionReferenceImageBase";
import { FM_HEADER_TOP_0 } from "domains/inference/constants/fileManagerStyle";
import {
  SectionsProvider,
  useSectionsContext,
} from "domains/inference/contexts/SectionsProvider";
import {
  JobSessionProvider,
  useJobSessionContext,
} from "domains/jobs/contexts/JobSessionProviderV2";
import { useScenarioToast } from "domains/notification/hooks/useScenarioToast";
import { LaunchShortcut } from "domains/shortcuts/components/Shortcut";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import ButtonWithCuIndicator from "domains/ui/components/ButtonWithCuIndicator";
import Divider from "domains/ui/components/Divider";
import {
  GeneratorContainer,
  GeneratorContent,
  GeneratorSidebar,
} from "domains/ui/components/GeneratorLayout";
import MenuItem from "domains/ui/components/Menu/MenuItem";
import PixelateParams from "domains/ui/components/PixelateParams";
import SidebarTitle from "domains/ui/components/Sidebar/SidebarTitle";
import { AnalyticsEvents } from "infra/analytics/constants/Events";
import Track from "infra/analytics/Track";
import { GetAssetsByAssetIdApiResponse } from "infra/api/generated/api";
import _ from "lodash";

export default function ImagePixelatePage() {
  return (
    <>
      <NextSeo title="Pixelate" />

      <SectionsProvider>
        <JobSessionProvider
          forcedFilterTypeOptions={[
            ...FILTER_IMAGE_TYPES_KEYS_ONLY_GENERATIONS,
          ]}
        >
          <ImagePixelate />
        </JobSessionProvider>
      </SectionsProvider>
    </>
  );
}

// ------------------------------------

export function ImagePixelate() {
  const { infoToast } = useScenarioToast();
  const { openSection } = useSectionsContext();
  const { files, jobs, isLoading, fileManagerProps, addJob } =
    useJobSessionContext();
  const { form, setValue, setReference, handlePixelateJob, isPixelateLoading } =
    useAssetPixelate();
  const scrollRef = useRef<HTMLDivElement>(null);

  const { isCuLoading, cuCost } = useAssetPixelateCu(form.reference?.id, form);

  const isPixelateButtonDisabled = !form.reference;

  const placeholders = useGetFileImagePlaceholders({
    length: 1,
    dimensions: {
      width: 512,
      height: 512,
    },
  });

  // ----------------------------------

  const handleMoreClick = useCallback(
    (key: string, value: any) => {
      if (key === "pixelGridSize") {
        setValue(key, value);
        openSection("settings");
      }

      infoToast({
        title: "Settings updated",
      });
    },
    [infoToast, openSection, setValue]
  );

  const handleRefineClick = useCallback<
    CardActionsCustomProps["onRefineClick"]
  >(
    async (asset) => {
      await setReference(asset);

      infoToast({
        title: "Settings updated",
      });
    },
    [setReference, infoToast]
  );

  // ----------------------------------

  const pixelateImage = useCallback(async () => {
    if (!form.reference) {
      return;
    }
    const job = await handlePixelateJob({
      assetId: form.reference.id,
      trackingExtraParams: {
        origin: "pixelate page",
      },
    });
    if (job) addJob(job);
  }, [form.reference, handlePixelateJob, addJob]);

  // ----------------------------------

  const listActionsProps = useMemo(
    () => ({
      onMoreClick: handleMoreClick,
    }),
    [handleMoreClick]
  );

  const cardActionsProps = useMemo(
    () => ({
      onRefineClick: handleRefineClick,
    }),
    [handleRefineClick]
  );

  // ----------------------------------

  return (
    <GeneratorContainer onSubmit={pixelateImage}>
      <GeneratorSidebar
        pb={form.shouldUseNewColorPalette ? "100px" : undefined}
      >
        <SidebarTitle
          title="Pixelate"
          videoUrl="https://www.youtube.com/embed/MFkITxCaH30?si=0NinWtL08a6jewKZ"
          videoTooltip="Watch a quick video to learn about Pixelate"
        />
        <Divider />

        <SectionReferenceImageBase
          id="referenceImage"
          title="Input Image"
          asset={form.reference}
          setAsset={setReference}
          removeBackgroundTrackingExtraParams={{
            origin: "pixelate page",
          }}
          selectLibrary={{
            tabs: ["image"],
          }}
          withSketch="never"
        />
        <Divider />

        <SidebarSection>
          <ButtonWithCuIndicator
            cuCost={cuCost}
            isCuLoading={isCuLoading}
            onClick={pixelateImage}
            isLoading={isPixelateLoading}
            isDisabled={isPixelateButtonDisabled}
            w={"100%"}
            data-testid="pixelate-action-button"
            tooltip={!isPixelateButtonDisabled && <LaunchShortcut />}
            tooltipProps={{
              placement: "right",
              hasArrow: true,
              isDisabled: isPixelateButtonDisabled,
            }}
          >
            Pixelate
          </ButtonWithCuIndicator>
        </SidebarSection>
        <Divider />

        <SidebarSection
          headerProps={{
            title: "Settings",
            id: "settings",
          }}
        >
          <PixelateParams form={form} setValue={setValue} />
        </SidebarSection>
        <Divider />
      </GeneratorSidebar>

      <GeneratorContent scrollRef={scrollRef}>
        <FileManagerImage
          scrollRef={scrollRef}
          files={
            !files?.length && !jobs?.length && !isLoading ? placeholders : files
          }
          jobs={jobs}
          isSelectable
          isLoading={isLoading}
          ListActionsComponent={ListActions}
          listActionsProps={listActionsProps}
          CardActionsComponent={CardActions}
          cardActionsProps={cardActionsProps}
          {...fileManagerProps}
          styleProps={FM_HEADER_TOP_0}
        />
      </GeneratorContent>
    </GeneratorContainer>
  );
}

// ------------------------------------

export type CardActionsCustomProps = {
  onRefineClick: (asset: GetAssetsByAssetIdApiResponse["asset"]) => void;
};

export type CardActionsProps = FmImageCardActionsProps<CardActionsCustomProps>;

function CardActions({ asset, top, bottom, onRefineClick }: CardActionsProps) {
  const canReuseImage = !["vectorization"].includes(asset?.metadata.type ?? "");

  const handleImageClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      e.stopPropagation();
      Track(AnalyticsEvents.CardAction.ReusedImage);
      if (asset) {
        onRefineClick(asset);
      }
    },
    [onRefineClick, asset]
  );

  if (!canReuseImage) {
    return null;
  }

  return (
    <>
      {top}

      <MenuItem
        w="170px"
        iconId="Nav/Image/Outline"
        iconH="12px"
        onClick={handleImageClick}
        text="Image"
      />

      {bottom}
    </>
  );
}

// ------------------------------------

export type ListActionsCustomProps = {
  onMoreClick: (key: string, value: any) => void;
};

export type ListActionsProps = FmImageListActionsProps<ListActionsCustomProps>;

function ListActions({ job, onMoreClick }: ListActionsProps) {
  const { isReader } = useTeamContext();
  const jobInput = job?.metadata.input as any;
  const colorPalette = jobInput?.colorPalette;
  const pixelGridSize = jobInput?.pixelGridSize;
  const isMoreDisplayed = job?.jobType === "pixelate" && !isReader;

  const moreOptions = useMemo(() => {
    if (!isMoreDisplayed) {
      return [];
    }
    return _.compact([
      colorPalette !== undefined && {
        id: "colorPalette",
        label: "Color Palette",
        value: `${colorPalette.length ?? 0} colors`,
      },
      pixelGridSize !== undefined && {
        id: "pixelGridSize",
        label: "Pixel Grid Size",
        value: `${pixelGridSize}px`,
        onClick: () => onMoreClick("pixelGridSize", pixelGridSize),
      },
    ]);
  }, [colorPalette, pixelGridSize, onMoreClick, isMoreDisplayed]);

  if (!isMoreDisplayed) {
    return null;
  }

  return <MoreTooltip options={moreOptions} />;
}
